import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
    CRISP_RUNTIME_CONFIG: any;
  }
}

interface CrispChatProps {
  websiteId?: string;
  tokenId?: string;
  sessionMerge?: boolean;
  autoload?: boolean;
  onChatOpened?: () => void;
  onChatClosed?: () => void;
  onMessageSent?: (message: string) => void;
  onMessageReceived?: (message: string) => void;
}

export const CrispChat: React.FC<CrispChatProps> = ({
  websiteId = "b8813a05-2dcc-4f47-9cab-8ca57a871caf",
  tokenId,
  sessionMerge = true,
  autoload = true,
  onChatOpened,
  onChatClosed,
  onMessageSent,
  onMessageReceived
}) => {
  const isInitialized = useRef(false);

  useEffect(() => {
    // Initialize Crisp if not already done
    if (!isInitialized.current && !window.$crisp) {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = websiteId;
      
      // Load Crisp script
      const script = document.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;
      document.head.appendChild(script);
      
      isInitialized.current = true;
    }

    // Wait for Crisp to be ready
    const initCrisp = () => {
      if (window.$crisp && window.$crisp.push) {
        // Configure Crisp
        window.$crisp.push(['safe', true]);
        
        if (tokenId) {
          window.$crisp.push(['set', 'session:data', [['tokenId', tokenId]]]);
        }
        
        if (sessionMerge !== undefined) {
          window.$crisp.push(['set', 'session:merge', [sessionMerge]]);
        }

        // Set up event listeners
        if (onChatOpened) {
          window.$crisp.push(['on', 'chat:opened', onChatOpened]);
        }
        
        if (onChatClosed) {
          window.$crisp.push(['on', 'chat:closed', onChatClosed]);
        }
        
        if (onMessageSent) {
          window.$crisp.push(['on', 'message:sent', onMessageSent]);
        }
        
        if (onMessageReceived) {
          window.$crisp.push(['on', 'message:received', onMessageReceived]);
        }

        // Auto-load chat if enabled
        if (autoload) {
          window.$crisp.push(['do', 'chat:open']);
        }
      } else {
        // Retry after a short delay
        setTimeout(initCrisp, 100);
      }
    };

    // Start initialization
    if (window.$crisp && window.$crisp.push) {
      initCrisp();
    } else {
      // Wait for script to load
      const checkCrisp = setInterval(() => {
        if (window.$crisp && window.$crisp.push) {
          clearInterval(checkCrisp);
          initCrisp();
        }
      }, 100);
    }

    // Cleanup function
    return () => {
      // Remove event listeners if needed
      if (window.$crisp && window.$crisp.push) {
        if (onChatOpened) {
          window.$crisp.push(['off', 'chat:opened', onChatOpened]);
        }
        if (onChatClosed) {
          window.$crisp.push(['off', 'chat:closed', onChatClosed]);
        }
        if (onMessageSent) {
          window.$crisp.push(['off', 'message:sent', onMessageSent]);
        }
        if (onMessageReceived) {
          window.$crisp.push(['off', 'message:received', onMessageReceived]);
        }
      }
    };
  }, [websiteId, tokenId, sessionMerge, autoload, onChatOpened, onChatClosed, onMessageSent, onMessageReceived]);

  // Helper functions to control the chat widget
  const openChat = () => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  const closeChat = () => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:close']);
    }
  };

  const hideChat = () => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:hide']);
    }
  };

  const showChat = () => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:show']);
    }
  };

  const setUserEmail = (email: string) => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'user:email', [email]]);
    }
  };

  const setUserNickname = (nickname: string) => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'user:nickname', [nickname]]);
    }
  };

  const setSessionData = (key: string, value: string) => {
    if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'session:data', [[key, value]]]);
    }
  };

  // Expose methods globally for use in other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).CrispChat = {
        openChat,
        closeChat,
        hideChat,
        showChat,
        setUserEmail,
        setUserNickname,
        setSessionData
      };
    }
  }, []);

  // This component doesn't render anything visible
  return null;
};

export default CrispChat;
